import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useEffect, useState, useCallback } from "react";
import DwinityLoader from "../../components/DwinityLoader";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Network, DataSet } from "vis-network/standalone";
import { DarkGroups } from "../../components/graph/data/DarkGroups";
import { loadGraphWithJwt } from '../../utils/graph/graphloader2';
import { loadJwt } from '../auth/mobileMpcService';
import { addProperties2, calculateChildrenCount, GROUP_FULL, GROUP_EMPTY, findChildrenOfNodeById2 } from '../../utils/graph/graphmodifier2';
import { getOnlyNumbers } from '../../utils/utils';
import { darkColors } from '../../data/SampleData';
import mini_human_dark from "../../assets/media/images/twin-mini_dark.png";
const BUBBLE_COUNT_IN_GROUP = 20;
const GROUP_COUNT = 20;
const MAX_VISIBLE_NODES = 200;
const CATEGORY_COLORS = {
    0: '#a2e1ee',
    1: '#ffb8e0',
    2: '#ff4757',
    3: '#00E49C',
    4: '#2ecc71',
    5: '#A8A9FF',
    6: '#71D1FF',
    7: '#E9B8FF',
    8: '#C0C0C0',
};
function processGraphNodes2(originalGraph) {
    for (let node of originalGraph.nodes) {
        if (node.label !== undefined) {
            node.title = undefined;
        }
        if (node.cid > 9) {
            node.cid = 9;
        }
        if (node.level > 9) {
            node.level = 9;
        }
        if (node.cid === 0 && node.level === 1) {
            node.cid = 9;
        }
        if (node.cid === 0 && node.level === 0) {
            node.shape = "image";
            node.shapeProperties = { useImageSize: false };
        }
        addProperties2(node);
    }
    calculateChildrenCount(originalGraph);
    for (let node of originalGraph.nodes) {
        node.group =
            node.cid +
                "" +
                node.level +
                (node.childrenCount !== undefined && node.childrenCount > 0 ? GROUP_FULL : GROUP_EMPTY);
    }
    return originalGraph;
}
const visOptions = {
    autoResize: true,
    clickToUse: false,
    layout: { improvedLayout: false },
    groups: DarkGroups,
    nodes: { font: { vadjust: 5 }, scaling: { label: { enabled: true } } },
    edges: {
        arrows: { to: { enabled: false }, from: { enabled: false } },
        color: { color: "#777FAC", hover: "#FFF", highlight: "#FFF", inherit: "from", opacity: 1 },
        smooth: { enabled: false, type: "dynamic", roundness: 0.5 },
        width: 0.5,
        selectionWidth: 3,
        hoverWidth: 1,
        length: 250,
        physics: true,
    },
    interaction: {
        navigationButtons: false,
        hover: true,
        hoverConnectedEdges: true,
        tooltipDelay: 999999,
        zoomSpeed: 1,
    },
    physics: {
        enabled: true,
        barnesHut: {
            theta: 0.5,
            gravitationalConstant: -2500,
            centralGravity: 0.4,
            springLength: 120,
            springConstant: 0.06,
            damping: 0.1,
            avoidOverlap: 0,
        },
        maxVelocity: 40,
        minVelocity: 20,
        solver: "barnesHut",
        stabilization: {
            enabled: true,
            iterations: 1000,
            updateInterval: 100,
            onlyDynamicEdges: false,
            fit: true,
        },
        timestep: 0.6,
        adaptiveTimestep: true,
    },
    width: "100%",
    height: "100%",
    manipulation: { enabled: false },
};
const MobileGraphPage = () => {
    const { t } = useTranslation();
    const containerRef = useRef(null);
    const networkRef = useRef(null);
    const graphDataRef = useRef(null);
    const nodesDataSetRef = useRef(null);
    const edgesDataSetRef = useRef(null);
    const allNodesRef = useRef([]);
    const allEdgesRef = useRef([]);
    const groupedArrayRef = useRef([]);
    const [selectedNode, setSelectedNode] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [isEmpty, setIsEmpty] = useState(false);
    const [dataReady, setDataReady] = useState(false);
    const [activeCategory, setActiveCategory] = useState("all");
    const [categories, setCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [showFiles, setShowFiles] = useState(false);
    const [showFilterPopup, setShowFilterPopup] = useState(false);
    const onBubleChildren = useCallback((initialNodeId, graphData) => {
        const done = [];
        const todo = [initialNodeId];
        const childrenIds = [];
        while (todo.length > 0) {
            const processingId = todo.shift();
            if (done.includes(processingId))
                continue;
            done.push(processingId);
            try {
                const children = findChildrenOfNodeById2(graphData, processingId);
                for (const child of children) {
                    if (!done.includes(child.id) && !childrenIds.includes(child.id)) {
                        childrenIds.push(child.id);
                        todo.push(child.id);
                    }
                }
            }
            catch (e) {
                // gracefully handle if aux structures not available
            }
        }
        return childrenIds;
    }, []);
    const clusterByCid = useCallback((id, network, hidden) => {
        const graphData = graphDataRef.current;
        if (!graphData)
            return;
        const numId = getOnlyNumbers(id);
        const initialNode = graphData.nodes.find((k) => k.id === numId);
        if (!initialNode)
            return;
        let actuelleColor = "#ffffff";
        const groupKey = initialNode.group?.replace("b", GROUP_FULL);
        const colorEntry = darkColors.filter((k) => k.group === groupKey)[0];
        if (colorEntry) {
            actuelleColor = colorEntry.background;
        }
        const nodeIsArray = onBubleChildren(numId, graphData);
        nodeIsArray.push(id);
        const clusterOptionsByData = {
            joinCondition: function (childOptions) {
                return childOptions.cid == initialNode.cid && nodeIsArray.includes(getOnlyNumbers(childOptions.id));
            },
            clusterNodeProperties: {
                id: "cidCluster" + id,
                hidden: hidden ? true : false,
                group: initialNode.group + "diamond",
                label: initialNode.label + " (" + (nodeIsArray.length - 1).toString() + ")",
                title: undefined,
                cid: initialNode.cid,
                size: 40,
                font: { color: actuelleColor },
            },
        };
        try {
            network.cluster(clusterOptionsByData);
        }
        catch (e) {
            // ignore clustering errors
        }
    }, [onBubleChildren]);
    const clusterByNode = useCallback((object, network) => {
        const graphData = graphDataRef.current;
        if (!graphData)
            return;
        const initialNode = graphData.nodes.find((k) => k.id === object.parentId);
        if (!initialNode)
            return;
        let actuelleColor = "#ffffff";
        const groupKey = initialNode.cid + "" + initialNode.level + GROUP_FULL;
        const colorEntry = darkColors.filter((k) => k.group === groupKey)[0];
        if (colorEntry) {
            actuelleColor = colorEntry.background || colorEntry.color;
        }
        const level = initialNode.level + 1;
        const clusterOptionsByData = {
            joinCondition: function (childOptions) {
                let childOptionsId = parseInt(childOptions.id);
                if (isNaN(childOptionsId))
                    childOptionsId = childOptions.id;
                return childOptions.cid == initialNode.cid && object.array.includes(childOptionsId);
            },
            clusterNodeProperties: {
                id: object.groupId,
                group: initialNode.cid + "" + level + GROUP_FULL + "group",
                label: object.label,
                title: undefined,
                cid: initialNode.cid,
                size: 40,
                font: { color: actuelleColor },
            },
        };
        try {
            network.cluster(clusterOptionsByData);
        }
        catch (e) {
            // ignore clustering errors
        }
    }, []);
    const mergeChildrenIdGroups = useCallback((source, groupCount) => {
        const targetArray = [];
        for (let i = 0; i < source.length; i += groupCount) {
            const current = source[i];
            const target = {
                id: current.id,
                groupId: current.firstLevel ? current.groupId : "super" + current.groupId,
                parentId: current.parentId,
                label: current.label,
                array: current.firstLevel ? [...current.array] : [current.groupId],
            };
            const childrenArray = [];
            for (let j = 1; j < groupCount && i + j < source.length; j++) {
                childrenArray.push(source[i + j]);
            }
            if (childrenArray.length > 0) {
                const lastIndex = childrenArray.length - 1;
                target.label =
                    target.label.substring(0, target.label.indexOf("-")) +
                        childrenArray[lastIndex].label.substring(childrenArray[lastIndex].label.indexOf("-"));
                for (const childObj of childrenArray) {
                    if (childObj.firstLevel) {
                        target.array.push(...childObj.array);
                    }
                    else {
                        target.array.push(childObj.groupId);
                    }
                }
            }
            targetArray.push(target);
        }
        return targetArray;
    }, []);
    const makeMultiLevelGroups = useCallback((idsArray, parentNodeId) => {
        const childrenIdGroupsArray = idsArray.map((id, idx) => ({
            id: id,
            groupId: "groupCluster" + id,
            parentId: parentNodeId,
            label: (idx + 1) + "-" + (idx + 1),
            array: [id],
            firstLevel: true,
        }));
        const levels = [];
        let level = childrenIdGroupsArray;
        let nextLevel;
        do {
            nextLevel = mergeChildrenIdGroups(level, GROUP_COUNT);
            levels.push(nextLevel);
            level = nextLevel;
        } while (level.length > BUBBLE_COUNT_IN_GROUP);
        return levels.flat();
    }, [mergeChildrenIdGroups]);
    const groupingOfbubbles2 = useCallback((processedGraph, network) => {
        const finallyGroupedArray = [];
        try {
            const groupingNodes = processedGraph.nodes.filter((k) => {
                try {
                    return network.getConnectedNodes(k.id, "to").length > BUBBLE_COUNT_IN_GROUP;
                }
                catch {
                    return false;
                }
            });
            for (const node of groupingNodes) {
                const childrenIdsArray = findChildrenOfNodeById2(processedGraph, node.id).map((k) => k.id);
                const groupedArray = makeMultiLevelGroups(childrenIdsArray, node.id);
                for (const groupObject of groupedArray) {
                    if (groupObject.array.length > 0) {
                        clusterByNode(groupObject, network);
                        finallyGroupedArray.push(groupObject);
                    }
                }
            }
        }
        catch (e) {
            console.warn('[MobileGraph] groupingOfbubbles2 error:', e);
        }
        groupedArrayRef.current = finallyGroupedArray;
    }, [makeMultiLevelGroups, clusterByNode]);
    const nodesReduction = useCallback((network, nodes) => {
        let bubleCount = 0;
        const levelSet = new Set(nodes.map((k) => k.level));
        const levelArray = Array.from(levelSet);
        levelArray.sort((a, b) => a - b);
        let number = 0;
        while (bubleCount < MAX_VISIBLE_NODES && number < levelArray.length) {
            bubleCount += nodes.filter((k) => k.level === levelArray[number]).length;
            number++;
        }
        if (number < levelArray.length) {
            const nodeIds = nodes.filter((k) => k.level === levelArray[number - 1]).map((k) => k.id);
            for (const nodeId of nodeIds) {
                clusterByCid(nodeId, network);
            }
        }
    }, [clusterByCid]);
    const clusterByFilter = useCallback((network, activeCid) => {
        const clusterOptionsByData = {
            joinCondition: function (childOptions) {
                return childOptions.cid !== activeCid && childOptions.cid !== 0;
            },
            clusterNodeProperties: {
                id: "cidClusterUser",
                hidden: true,
                shape: "diamond",
            },
        };
        try {
            network.cluster(clusterOptionsByData);
        }
        catch (e) {
            // ignore
        }
    }, []);
    const loadGraphData = useCallback(async () => {
        setIsLoading(true);
        setLoadError(null);
        setIsEmpty(false);
        setDataReady(false);
        try {
            const jwt = await loadJwt();
            if (!jwt) {
                setLoadError(t('mobile.graph.not_authenticated'));
                setIsLoading(false);
                return;
            }
            const graphJsonString = await loadGraphWithJwt(jwt);
            if (graphJsonString === undefined) {
                setIsEmpty(true);
                setIsLoading(false);
                return;
            }
            let graphData;
            try {
                graphData = typeof graphJsonString === 'string' ? JSON.parse(graphJsonString) : graphJsonString;
            }
            catch (parseError) {
                setLoadError(t('mobile.graph.parse_error'));
                setIsLoading(false);
                return;
            }
            if (!graphData || !graphData.nodes || !graphData.edges) {
                setIsEmpty(true);
                setIsLoading(false);
                return;
            }
            const processedGraph = processGraphNodes2(graphData);
            for (const node of processedGraph.nodes) {
                if (node.cid === 0 && node.level === 0) {
                    node.image = mini_human_dark;
                }
            }
            graphDataRef.current = processedGraph;
            allNodesRef.current = processedGraph.nodes;
            allEdgesRef.current = processedGraph.edges;
            const cidMap = new Map();
            for (const node of processedGraph.nodes) {
                if (node.cid !== undefined && node.cid > 0 && !cidMap.has(node.cid)) {
                    cidMap.set(node.cid, node.label || `Category ${node.cid}`);
                }
            }
            const rootNodes = processedGraph.nodes.filter((n) => n.level === 1 && n.cid > 0);
            for (const rn of rootNodes) {
                if (cidMap.has(rn.cid)) {
                    cidMap.set(rn.cid, rn.label);
                }
            }
            const cats = Array.from(cidMap.entries()).map(([cid, label]) => ({ cid, label }));
            cats.sort((a, b) => a.cid - b.cid);
            setCategories(cats);
            const nodesDS = new DataSet(processedGraph.nodes);
            const nodeIds = new Set(nodesDS.getIds());
            const filteredEdges = processedGraph.edges.filter((e) => nodeIds.has(e.from) && nodeIds.has(e.to));
            const edgesDS = new DataSet(filteredEdges);
            nodesDataSetRef.current = nodesDS;
            edgesDataSetRef.current = edgesDS;
            if (processedGraph.nodes.length === 0) {
                setIsEmpty(true);
            }
            else {
                setDataReady(true);
            }
        }
        catch (error) {
            console.error('[MobileGraph] Load error:', error);
            setLoadError(error?.message || t('mobile.graph.error'));
        }
        finally {
            setIsLoading(false);
        }
    }, [t]);
    useEffect(() => {
        loadGraphData();
    }, [loadGraphData]);
    useEffect(() => {
        if (!dataReady || !nodesDataSetRef.current || !edgesDataSetRef.current)
            return;
        if (!containerRef.current)
            return;
        if (networkRef.current) {
            networkRef.current.destroy();
            networkRef.current = null;
        }
        const initNetwork = () => {
            if (!containerRef.current)
                return;
            const rect = containerRef.current.getBoundingClientRect();
            console.log('[MobileGraph] Container size:', rect.width, 'x', rect.height);
            console.log('[MobileGraph] Nodes:', nodesDataSetRef.current?.length, 'Edges:', edgesDataSetRef.current?.length);
            const network = new Network(containerRef.current, { nodes: nodesDataSetRef.current, edges: edgesDataSetRef.current }, visOptions);
            networkRef.current = network;
            let longPressTimer = null;
            let longPressTriggered = false;
            const canvasEl = containerRef.current?.querySelector('canvas');
            if (canvasEl) {
                canvasEl.addEventListener('touchstart', (e) => {
                    longPressTriggered = false;
                    const touch = e.touches[0];
                    const domPos = { x: touch.clientX - canvasEl.getBoundingClientRect().left, y: touch.clientY - canvasEl.getBoundingClientRect().top };
                    const nodeId = network.getNodeAt(domPos);
                    if (nodeId !== undefined) {
                        longPressTimer = setTimeout(() => {
                            longPressTriggered = true;
                            const node = allNodesRef.current.find((n) => n.id === nodeId);
                            if (node)
                                setSelectedNode(node);
                        }, 500);
                    }
                }, { passive: true });
                canvasEl.addEventListener('touchmove', () => {
                    if (longPressTimer) {
                        clearTimeout(longPressTimer);
                        longPressTimer = null;
                    }
                }, { passive: true });
                canvasEl.addEventListener('touchend', () => {
                    if (longPressTimer) {
                        clearTimeout(longPressTimer);
                        longPressTimer = null;
                    }
                }, { passive: true });
            }
            network.on("click", (params) => {
                if (longPressTriggered) {
                    longPressTriggered = false;
                    return;
                }
                if (params.nodes.length === 0) {
                    setSelectedNode(null);
                }
            });
            network.on("doubleClick", (params) => {
                if (params.nodes.length > 0) {
                    const nodeId = params.nodes[0];
                    setSelectedNode(null);
                    try {
                        if (network.isCluster(nodeId)) {
                            network.openCluster(nodeId);
                            setTimeout(() => network.fit(), 100);
                        }
                        else {
                            clusterByCid(nodeId, network);
                            setTimeout(() => network.fit(), 100);
                        }
                    }
                    catch (e) {
                        // ignore
                    }
                }
            });
            network.on("stabilizationIterationsDone", () => {
                console.log('[MobileGraph] Stabilization done');
            });
            network.once("afterDrawing", () => {
                console.log('[MobileGraph] First draw complete');
            });
            const graphData = graphDataRef.current;
            if (graphData) {
                groupingOfbubbles2(graphData, network);
                const userFilesNode = graphData.nodes.find((k) => k.label === "User Files");
                if (userFilesNode) {
                    clusterByCid(userFilesNode.id, network, true);
                }
                const level1Nodes = graphData.nodes.filter((k) => k.level === 1).map((k) => k.id);
                for (const nodeId of level1Nodes) {
                    clusterByCid(nodeId, network);
                }
                nodesReduction(network, graphData.nodes);
            }
            setTimeout(() => {
                network.fit();
            }, 200);
        };
        requestAnimationFrame(() => {
            requestAnimationFrame(initNetwork);
        });
        return () => {
            if (networkRef.current) {
                networkRef.current.destroy();
                networkRef.current = null;
            }
        };
    }, [dataReady, clusterByCid, groupingOfbubbles2, nodesReduction]);
    const filterCategory = useCallback((cat) => {
        setActiveCategory(cat);
        const network = networkRef.current;
        if (!network)
            return;
        try {
            if (network.isCluster("cidClusterUser")) {
                network.openCluster("cidClusterUser");
            }
        }
        catch (e) {
            // ignore
        }
        if (cat === "all") {
            nodesReduction(network, allNodesRef.current);
            setTimeout(() => network.fit(), 100);
        }
        else {
            const cidNum = parseInt(cat, 10);
            clusterByFilter(network, cidNum);
            setTimeout(() => network.fit(), 100);
        }
        setSelectedNode(null);
    }, [nodesReduction, clusterByFilter]);
    const handleExpandAll = useCallback(() => {
        const network = networkRef.current;
        const graphData = graphDataRef.current;
        if (!network || !graphData)
            return;
        const clusteredNodes = graphData.nodes.filter((k) => k.cid >= 1).map((k) => k.id);
        for (const nodeId of clusteredNodes) {
            try {
                if (network.isCluster("cidCluster" + nodeId)) {
                    network.openCluster("cidCluster" + nodeId);
                }
            }
            catch (e) {
                // ignore
            }
        }
        const grouped = groupedArrayRef.current;
        if (grouped && grouped.length > 0) {
            for (const groupObject of grouped) {
                try {
                    if (network.isCluster(groupObject.groupId)) {
                        network.openCluster(groupObject.groupId);
                    }
                }
                catch (e) {
                    // ignore
                }
            }
        }
        if (!showFiles) {
            const userFilesNode = graphData.nodes.find((k) => k.label === "User Files");
            if (userFilesNode) {
                clusterByCid(userFilesNode.id, network, true);
            }
        }
        const fitToAll = () => {
            network.fit({ animation: { duration: 800, easingFunction: 'easeInOutQuad' } });
        };
        network.once('stabilized', () => { setTimeout(fitToAll, 100); });
        setTimeout(fitToAll, 1500);
    }, [showFiles, clusterByCid]);
    const handleCollapseAll = useCallback(() => {
        const network = networkRef.current;
        const graphData = graphDataRef.current;
        if (!network || !graphData)
            return;
        groupingOfbubbles2(graphData, network);
        if (!showFiles) {
            const userFilesNode = graphData.nodes.find((k) => k.label === "User Files");
            if (userFilesNode) {
                clusterByCid(userFilesNode.id, network, true);
            }
        }
        const level1Nodes = graphData.nodes.filter((k) => k.level === 1).map((k) => k.id);
        for (const nodeId of level1Nodes) {
            clusterByCid(nodeId, network);
        }
        setTimeout(() => network.fit(), 100);
    }, [showFiles, clusterByCid, groupingOfbubbles2]);
    const handleToggleFiles = useCallback(() => {
        const network = networkRef.current;
        const graphData = graphDataRef.current;
        if (!network || !graphData)
            return;
        const userFilesNode = graphData.nodes.find((k) => k.label === "User Files");
        if (!userFilesNode)
            return;
        if (showFiles) {
            clusterByCid(userFilesNode.id, network, true);
            setShowFiles(false);
        }
        else {
            try {
                if (network.isCluster("cidCluster" + userFilesNode.id)) {
                    network.openCluster("cidCluster" + userFilesNode.id);
                }
            }
            catch (e) {
                // ignore
            }
            setShowFiles(true);
        }
        setTimeout(() => network.fit(), 100);
    }, [showFiles, clusterByCid]);
    const handleSearch = useCallback((e) => {
        const term = e.target.value;
        setSearchTerm(term);
        if (!networkRef.current || !nodesDataSetRef.current)
            return;
        if (!term.trim()) {
            networkRef.current.unselectAll();
            return;
        }
        const lowerTerm = term.toLowerCase();
        const matchingIds = allNodesRef.current
            .filter((n) => n.label?.toLowerCase().includes(lowerTerm))
            .map((n) => n.id);
        if (matchingIds.length > 0) {
            networkRef.current.selectNodes(matchingIds);
            networkRef.current.focus(matchingIds[0], { scale: 1.2, animation: true });
        }
        else {
            networkRef.current.unselectAll();
        }
    }, []);
    const getConnectionCount = useCallback((nodeId) => {
        return allEdgesRef.current.filter((e) => e.from === nodeId || e.to === nodeId).length;
    }, []);
    const getCategoryLabel = useCallback((label) => {
        const key = `mobile.graph.categories.${label.toLowerCase()}`;
        const translated = t(key);
        return translated !== key ? translated : label;
    }, [t]);
    const getNodeCountForCid = useCallback((cid) => {
        return allNodesRef.current.filter((n) => n.cid === cid).length;
    }, []);
    const getActiveCategoryLabel = useCallback(() => {
        if (activeCategory === 'all')
            return t('mobile.graph.categories.everything');
        const cat = categories.find(c => String(c.cid) === activeCategory);
        return cat ? getCategoryLabel(cat.label) : t('mobile.graph.categories.everything');
    }, [activeCategory, categories, getCategoryLabel, t]);
    const getActiveCategoryColor = useCallback(() => {
        if (activeCategory === 'all')
            return CATEGORY_COLORS[0];
        const cidNum = parseInt(activeCategory, 10);
        return CATEGORY_COLORS[cidNum] || CATEGORY_COLORS[0];
    }, [activeCategory]);
    if (isLoading) {
        return (_jsxs("div", { className: "mobile-graph", children: [_jsx("div", { className: "mobile-graph-header", children: _jsx("h1", { className: "mobile-graph-title", children: t('mobile.graph.title') }) }), _jsxs("div", { style: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }, children: [_jsx(DwinityLoader, { size: 48, fullScreen: false, inline: true }), _jsx("span", { style: { color: '#888' }, children: t('mobile.graph.loading') })] })] }));
    }
    if (loadError) {
        return (_jsxs("div", { className: "mobile-graph", children: [_jsx("div", { className: "mobile-graph-header", children: _jsx("h1", { className: "mobile-graph-title", children: t('mobile.graph.title') }) }), _jsxs("div", { style: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', color: '#ff6b6b' }, children: [_jsx("span", { children: loadError || t('mobile.graph.error') }), _jsx("button", { onClick: loadGraphData, style: { padding: '8px 20px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }, children: t('mobile.graph.retry') })] })] }));
    }
    if (isEmpty) {
        return (_jsxs("div", { className: "mobile-graph", children: [_jsx("div", { className: "mobile-graph-header", children: _jsx("h1", { className: "mobile-graph-title", children: t('mobile.graph.title') }) }), _jsxs("div", { style: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#666' }, children: [_jsx("span", { children: t('mobile.graph.empty') }), _jsx("span", { style: { fontSize: '13px' }, children: t('mobile.graph.empty_hint') })] })] }));
    }
    return (_jsxs("div", { className: "mobile-graph", children: [_jsx("div", { className: "mobile-graph-header", children: _jsx("h1", { className: "mobile-graph-title", children: t('mobile.graph.title') }) }), _jsx("div", { className: "mobile-graph-toolbar", children: _jsxs("button", { className: "mobile-graph-filter-btn", onClick: () => setShowFilterPopup(true), children: [_jsx("span", { className: "filter-btn-dot", style: { backgroundColor: getActiveCategoryColor() } }), getActiveCategoryLabel(), _jsx("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("polyline", { points: "6 9 12 15 18 9" }) })] }) }), showFilterPopup && createPortal(_jsx("div", { className: "mobile-graph-filter-popup-overlay", onClick: () => setShowFilterPopup(false), children: _jsxs("div", { className: "mobile-graph-filter-popup", onClick: (e) => e.stopPropagation(), children: [_jsx("div", { className: "filter-popup-handle" }), _jsx("div", { className: "filter-popup-title", children: t('mobile.graph.filter') }), _jsxs("div", { className: `filter-popup-item${activeCategory === 'all' ? ' active' : ''}`, onClick: () => { filterCategory('all'); setShowFilterPopup(false); }, children: [_jsx("div", { className: "filter-color-ring", style: { color: CATEGORY_COLORS[0] } }), _jsx("span", { className: "filter-item-label", children: t('mobile.graph.categories.everything') }), _jsx("span", { className: "filter-item-count", children: allNodesRef.current.length })] }), categories.map((cat) => (_jsxs("div", { className: `filter-popup-item${activeCategory === String(cat.cid) ? ' active' : ''}`, onClick: () => { filterCategory(String(cat.cid)); setShowFilterPopup(false); }, children: [_jsx("div", { className: "filter-color-ring", style: { color: CATEGORY_COLORS[cat.cid] || '#888' } }), _jsx("span", { className: "filter-item-label", children: getCategoryLabel(cat.label) }), _jsx("span", { className: "filter-item-count", children: getNodeCountForCid(cat.cid) })] }, cat.cid))), _jsx("div", { className: "filter-popup-divider" }), _jsxs("label", { className: "filter-popup-files-toggle", onClick: (e) => e.stopPropagation(), children: [_jsx("input", { type: "checkbox", checked: showFiles, onChange: handleToggleFiles }), _jsx("span", { children: t('mobile.graph.show_files') })] })] }) }), document.body), _jsx("div", { className: "mobile-graph-search", children: _jsx("input", { type: "text", placeholder: t('mobile.graph.search_placeholder'), value: searchTerm, onChange: handleSearch }) }), _jsx("div", { className: "mobile-graph-canvas-container", children: _jsx("div", { ref: containerRef, style: { width: '100%', height: '100%' } }) }), _jsxs("div", { className: "mobile-graph-bottom-controls", children: [_jsxs("button", { className: "mobile-graph-bottom-btn", onClick: handleExpandAll, children: [_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("polyline", { points: "15 3 21 3 21 9" }), _jsx("polyline", { points: "9 21 3 21 3 15" }), _jsx("line", { x1: "21", y1: "3", x2: "14", y2: "10" }), _jsx("line", { x1: "3", y1: "21", x2: "10", y2: "14" })] }), t('mobile.graph.expand_all')] }), _jsxs("button", { className: "mobile-graph-bottom-btn", onClick: handleCollapseAll, children: [_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("polyline", { points: "4 14 10 14 10 20" }), _jsx("polyline", { points: "20 10 14 10 14 4" }), _jsx("line", { x1: "14", y1: "10", x2: "21", y2: "3" }), _jsx("line", { x1: "3", y1: "21", x2: "10", y2: "14" })] }), t('mobile.graph.collapse_all')] })] }), selectedNode && (_jsxs("div", { className: "mobile-graph-info-panel", children: [_jsxs("div", { className: "mobile-graph-info-header", children: [_jsx("div", { className: "mobile-graph-info-name", children: selectedNode.label }), _jsx("button", { className: "mobile-graph-info-close", onClick: () => setSelectedNode(null), children: "\u2715" })] }), _jsxs("div", { className: "mobile-graph-info-details", children: [_jsxs("div", { className: "mobile-graph-info-row", children: [_jsx("span", { className: "mobile-graph-info-label", children: t('mobile.graph.type') }), _jsx("span", { className: "mobile-graph-info-value", children: selectedNode._type?.split('/').pop() || '-' })] }), _jsxs("div", { className: "mobile-graph-info-row", children: [_jsx("span", { className: "mobile-graph-info-label", children: t('mobile.graph.connections') }), _jsx("span", { className: "mobile-graph-info-value", children: getConnectionCount(selectedNode.id) })] }), _jsxs("div", { className: "mobile-graph-info-row", children: [_jsx("span", { className: "mobile-graph-info-label", children: t('mobile.graph.node_id') }), _jsx("span", { className: "mobile-graph-info-value", children: selectedNode.id })] }), _jsxs("div", { className: "mobile-graph-info-row", children: [_jsx("span", { className: "mobile-graph-info-label", children: t('mobile.graph.group') }), _jsx("span", { className: "mobile-graph-info-value", children: selectedNode.group || '-' })] }), selectedNode.childrenCount !== undefined && (_jsxs("div", { className: "mobile-graph-info-row", children: [_jsx("span", { className: "mobile-graph-info-label", children: t('mobile.graph.children') }), _jsx("span", { className: "mobile-graph-info-value", children: selectedNode.childrenCount })] }))] })] }))] }));
};
export default MobileGraphPage;
